import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Layers } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatTaka } from '@/utils/number';
import { pcmpRedisburseBatchesQueryOptions } from '../../api/pcmpAccounts.api';
import { PCMP_ACCOUNTS_EMPTY_MESSAGE } from '../../constants/pcmpAccounts.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import type { PcmpAccountsFilterController } from '../../hooks/usePcmpAccountsFilters';
import { toPcmpQueueSearch } from '../../hooks/usePcmpQueueFilters';
import type { PcmpAccountsBatchView, PcmpRedisburseBatch } from '../../types/pcmpAccounts.types';
import {
  PcmpBatchAmountCell,
  PcmpBatchItemsCell,
  PcmpBatchStepCell,
} from '../PcmpBatch/PcmpBatchCells';
import { PcmpEmptyCell } from '../PcmpQueue/PcmpQueueCells';

const columns: ColumnDef<PcmpRedisburseBatch>[] = [
  {
    id: 'batchNo',
    header: 'Batch',
    cell: ({ row }) => (
      <span className="font-mono font-medium whitespace-nowrap">#{row.original.batchNo}</span>
    ),
  },
  {
    id: 'parentBatchNo',
    header: 'From Batch',
    cell: ({ row }) =>
      row.original.parentBatchNo === null ? (
        <PcmpEmptyCell />
      ) : (
        <span className="font-mono whitespace-nowrap">#{row.original.parentBatchNo}</span>
      ),
  },
  {
    id: 'items',
    header: () => <div className="text-right">Proposals</div>,
    cell: ({ row }) => (
      <div className="text-right">
        <PcmpBatchItemsCell batch={row.original} />
      </div>
    ),
  },
  {
    id: 'amount',
    header: () => <div className="text-right">Amount</div>,
    cell: ({ row }) => (
      <div className="text-right whitespace-nowrap tabular-nums">
        {formatTaka(row.original.totalAmount)}
      </div>
    ),
  },
  {
    id: 'pending',
    header: () => <div className="text-right">Pending</div>,
    cell: ({ row }) => <PcmpBatchAmountCell value={row.original.pending} />,
  },
  {
    id: 'paid',
    header: () => <div className="text-right">Paid</div>,
    cell: ({ row }) => <PcmpBatchAmountCell value={row.original.paid} />,
  },
  {
    id: 'failed',
    header: () => <div className="text-right">Failed</div>,
    cell: ({ row }) => <PcmpBatchAmountCell value={row.original.failed} />,
  },
  {
    id: 'created',
    header: 'Created',
    cell: ({ row }) => (
      <PcmpBatchStepCell by={row.original.createdBy} at={row.original.createdAt} />
    ),
  },
];

interface PcmpRedisburseBatchesViewProps {
  controller: PcmpAccountsFilterController;
  view: PcmpAccountsBatchView;
}

/** Re-disbursement batches; a part-paid batch shows in both views. */
export function PcmpRedisburseBatchesView({ controller, view }: PcmpRedisburseBatchesViewProps) {
  const router = useRouter();
  const { tab, pageParams, appliedFilters, limit } = controller;

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpRedisburseBatchesQueryOptions({ ...pageParams, view }),
  );

  useKeepPageInRange(pageParams.page, data?.meta.totalPages, controller.setPage);

  const openBatch = useCallback(
    (batch: PcmpRedisburseBatch) => {
      void router.navigate({
        to: '/pcmp/accounts/redisburse-batches/$batchNo',
        params: { batchNo: batch.batchNo },
        // Open on the members of this list's view (still to pay, or paid).
        search: { ...toPcmpQueueSearch(appliedFilters), view },
      });
    },
    [router, appliedFilters, view],
  );

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load the re-disbursement batches"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        fill
      />
    );
  }

  return (
    <>
      <DataTable
        data={data?.data ?? []}
        columns={columns}
        isLoading={isLoading}
        fillHeight
        className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
        emptyIcon={Layers}
        emptyMessage={PCMP_ACCOUNTS_EMPTY_MESSAGE[tab]}
        skeletonRowCount={Math.min(limit, 15)}
        onRowClick={openBatch}
      />
      {data ? (
        <DataTablePagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={data.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}
    </>
  );
}
